// Shared "HR's org-wide case list" query — one implementation for HrCases.tsx
// (the full search/filter/paginated desktop list) and HrMobileCases.tsx (the
// mobile "Cases" tab's quick-glance default view). Previously HrMobileCases.tsx
// hand-rolled its own query under a deliberately-different key
// (`['hr','cases','mobile-glance']`), unlike `use-cases.ts`/
// `use-employee-portal.ts`'s unified-key approach for the equivalent Manager/
// Employee surfaces — this brings HR in line with that convention.
//
// Calling with no args reproduces HrCases.tsx's own default (unfiltered,
// page 0) request exactly, so the two naturally share one cache entry
// whenever desktop is in its default view — genuinely the same request, not
// just conveniently similar.

import { useQuery } from '@tanstack/react-query';
import type { CasesListResponse } from '@besideu/shared/cases';
import { api } from '@/lib/api.ts';

export const HR_CASES_PAGE_SIZE = 50;

export function useHrCases({
  q = '',
  status = '',
  managerId = '',
  bucket = '',
  page = 0,
}: {
  q?: string;
  status?: string;
  managerId?: string;
  /** Dashboard tile deep-link (`?bucket=onTrack|dueSoon|overdue|paused`) — a
   *  derived grouping, not a stored status; see `CaseBucket` in
   *  `@besideu/shared/cases`. */
  bucket?: string;
  page?: number;
} = {}) {
  return useQuery({
    queryKey: ['hr', 'cases', { q, status, managerId, bucket, page }],
    queryFn: () => {
      const params = new URLSearchParams();
      if (q) params.set('q', q);
      if (status) params.set('status', status);
      if (managerId) params.set('managerId', managerId);
      if (bucket) params.set('bucket', bucket);
      params.set('limit', String(HR_CASES_PAGE_SIZE));
      params.set('offset', String(page * HR_CASES_PAGE_SIZE));
      return api.get<CasesListResponse>(`/api/hr/cases?${params}`);
    },
    placeholderData: (prev) => prev,
  });
}
